import { get } from 'lodash-es';
import {
  parseBit,
  parseBoolean,
  parseCustom,
  parseEnum,
  parseNumber,
  parseString,
  parseWeek,
  parseNumberBySeparate,
  parseSignedNumber,
  parseBits,
} from './parseFun';
import { createMyGenerator } from './generator';
import { transformEnabled } from './condition';
import { getItemDefault } from './utils';
import { emitLog } from './log';

const parseTypes = {
  number: parseNumber,
  signedNumber: parseSignedNumber,
  string: parseString,
  boolean: parseBoolean,
  bit: parseBit,
  bits: parseBits,
  week: parseWeek,
  enum: parseEnum,
  custom: parseCustom,
  numberBySeparate: parseNumberBySeparate,
  default: parseNumber,
};

/**
 * 解析子节点字段
 * @param generator 截取数据生成器
 * @param config 配置
 * @param rootResult 已解析根数据
 * @returns 解析字段值
 */
const transformChild = (generator: MyGenerator, config: DpMapItem, rootResult: any) => {
  const { childMap } = config;
  const childResult: any = {};
  childMap.forEach(itemConfig => {
    const { condition } = itemConfig;
    if (transformEnabled(condition, rootResult, childResult)) {
      const value = transformProp(generator, itemConfig, childResult, rootResult);
      childResult[itemConfig.name] = value;
    }
  });
  return childResult;
};

/**
 * 解析字段
 * @param generator 截取数据生成器
 * @param config 配置
 * @param result 已解析数据
 * @param rootResult 已解析根数据
 * @returns 解析字段值
 */
const transformProp = (generator: MyGenerator, config: DpMapItem, result: any, rootResult: any) => {
  const { type = 'number', loop = false, limit, name } = config;
  let max = 1;
  // 处理循环情况
  if (loop) {
    // 是否限制循环次数
    if (limit) {
      // 是否由已解析的数据确认循环次数
      if (typeof limit === 'string' || Array.isArray(limit)) {
        max = get(result, limit, 0);
      } else {
        max = limit;
      }
    } else {
      max = Infinity;
    }
  }
  const values: any = [];
  for (let i = 0; i < max; i++) {
    if (generator.hasNext()) {
      let value: any;
      // 如果是子类型解析
      if (type === 'child') {
        value = transformChild(generator, config, rootResult);
      } else {
        // 其他数据类型处理
        if (typeof parseTypes[type] === 'function') {
          value = parseTypes[type](generator, config, result);
        } else {
          value = parseTypes.default(generator, config, result);
        }
      }
      values.push(value);
    } else {
      // 解析失败，返回默认值
      if (!values.length && type !== 'child') {
        values.push(getItemDefault(config, result));
      }

      // 提示解析有误
      i === 0 &&
        emitLog({
          type: 'warning',
          message: `解析到属性【${name}】时，发现已无可解析的数据，属性返回默认值`,
          data: { [name]: loop ? values : values[0] },
        });
      break;
    }
  }

  return loop ? values : values[0];
};

/**
 * 解析协议
 * @param config 总dp配置
 * @param dpValue 待解析的协议数据
 * @param result 已解析完成的数据
 */
const transform = (config: DpMap, dpValue: string, result: any) => {
  // 当 dp 配置为列表数据时，并且 dp 值为 00， 则表示其为空列表；（原因为raw格式数据必须有下发1字节数据，不能传空数据）
  if ((dpValue === '00' || !dpValue) && config[0]?.type === 'child' && config[0]?.loop) {
    return [];
  }
  const generator = createMyGenerator(dpValue);
  config.forEach((itemConfig: DpMapItem) => {
    const { name, condition } = itemConfig;
    if (transformEnabled(condition, result, result)) {
      // 执行解析
      const value = transformProp(generator, itemConfig, result, result);
      result[name] = value;
    }
  });

  return result;
};

export const parse = (value: string, dpMap: DpMap) => {
  return transform(dpMap, value, {});
};
